// components/comparison/FoodIntakeChart.jsx
import React, { useEffect, useMemo, useState } from "react";
import NativeAnalysis from "../NativeAnalysis";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Label,
} from "recharts";

const FIXED_METRICS = ["Min Target", "Max Target"];
const FEASIBLE_METRIC = "Feasible kcal";

/**
 * Fixed metrics: one line total (from first pathway with data).
 * Feasible kcal: one line per selected pathway.
 */
function buildSeries({ comparisonData, selectedPathways, chartKey }) {
  const firstWithData = (selectedPathways || []).find(
    (p) => comparisonData?.[p]?.charts?.[chartKey]?.labels?.length,
  );
  const labels = firstWithData
    ? comparisonData[firstWithData].charts[chartKey].labels
    : [];

  const rows = labels.map((year, idx) => {
    const row = { year };

    // Fixed metrics: take values from the first pathway only (they are the same for all)
    if (firstWithData) {
      const c = comparisonData[firstWithData].charts[chartKey];
      for (const metric of FIXED_METRICS) {
        const ds = c.datasets?.find((d) => d.label === metric);
        if (ds) row[metric] = ds.data?.[idx] ?? null;
      }
    }

    // Feasible kcal: one value per pathway
    for (const p of selectedPathways || []) {
      const c = comparisonData?.[p]?.charts?.[chartKey];
      if (!c?.labels?.length) continue;
      const ds = c.datasets?.find((d) => d.label === FEASIBLE_METRIC);
      if (ds) row[`${p} — ${FEASIBLE_METRIC}`] = ds.data?.[idx] ?? null;
    }

    return row;
  });

  // Fixed keys: one per metric (only those present in data)
  const fixedKeys = firstWithData
    ? FIXED_METRICS.filter((m) =>
        comparisonData[firstWithData].charts[chartKey]?.datasets?.some(
          (d) => d.label === m,
        ),
      )
    : [];

  // Feasible keys: one per pathway
  const feasibleKeys = [];
  for (const p of selectedPathways || []) {
    const c = comparisonData?.[p]?.charts?.[chartKey];
    if (c?.datasets?.some((d) => d.label === FEASIBLE_METRIC)) {
      feasibleKeys.push(`${p} — ${FEASIBLE_METRIC}`);
    }
  }

  return { rows, fixedKeys, feasibleKeys, keys: [...fixedKeys, ...feasibleKeys] };
}

/**
 * Colores por métrica fija
 */
const METRIC_COLOR = {
  "Healthy kcal": "#9999CC",
  "Min Target": "#ff0000",
  "Max Target": "#FFD786",
};

// Dashes por trayectoria (para diferenciar pathways en Feasible kcal)
const DASH_POOL = [""];

/** true if the key represents a fixed metric line */
function isFixedKey(k) {
  return FIXED_METRICS.includes(k);
}

/** For feasible keys, extract the pathway name */
function pathwayFromKey(k) {
  const sep = ` — ${FEASIBLE_METRIC}`;
  return k.endsWith(sep) ? k.slice(0, k.length - sep.length) : null;
}

// Tooltip más útil (agrupa y ordena por valor desc)
function CustomTooltip({ active, payload, label, getColorByName, pathwayIndex }) {
  if (!active || !payload?.length) return null;

  const sorted = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : -Infinity;
    const bv = typeof b.value === "number" ? b.value : -Infinity;
    return bv - av;
  });

  const getDash = (k) => {
    if (isFixedKey(k)) return "5 5"; // fixed lines dashed
    const pathway = pathwayFromKey(k);
    const idx = pathwayIndex?.get(pathway) ?? 0;
    return DASH_POOL[idx % DASH_POOL.length];
  };

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 shadow-md text-[11px]">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>
      {sorted.map((item) => {
        const dash = getDash(item.dataKey);
        return (
          <div
            key={item.dataKey}
            className="mb-0.5 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2">
              <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
                <line x1="0" y1="1" x2="16" y2="1" stroke={item.color} strokeWidth="2" strokeDasharray={dash} />
              </svg>
              <span className="text-[#7E8081]">{item.name}</span>
            </div>
            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? item.value.toLocaleString("en-US", { maximumFractionDigits: 2 })
                : item.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Leyenda clickable — todas las líneas (fijas y feasible) se pueden ocultar.
 */
function ClickableLegend({ payload, hidden, onToggle, pathwayIndex }) {
  if (!payload?.length) return null;

  // Deduplicate fixed metrics (same metric may appear per pathway in Recharts payload)
  const seen = new Set();
  const entries = [];
  for (const entry of payload) {
    const k = entry.dataKey;
    if (isFixedKey(k)) {
      if (!seen.has(k)) {
        seen.add(k);
        entries.push({ key: k, label: k, color: entry.color });
      }
    } else {
      const pathway = pathwayFromKey(k);
      const label = pathway ? `${pathway}` : k;
      entries.push({ key: k, label, color: entry.color });
    }
  }

  const getDash = (k) => {
    if (isFixedKey(k)) return "5 5"; // fixed lines dashed
    const pathway = pathwayFromKey(k);
    const idx = pathwayIndex?.get(pathway) ?? 0;
    return DASH_POOL[idx % DASH_POOL.length];
  };

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {entries.map((entry) => {
        const isHidden = hidden.has(entry.key);
        const dash = getDash(entry.key);
        return (
          <button
            key={entry.key}
            type="button"
            onClick={() => onToggle(entry.key)}
            className={`flex items-center gap-2 cursor-pointer ${isHidden ? "opacity-40" : ""}`}
            title="Click to show/hide"
          >
            <span className="text-[12px]">{entry.label}</span>
            <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
              <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color} strokeWidth="2" strokeDasharray={dash} />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

export default function FoodIntakeChart({ comparisonData, selectedPathways, getColorByName }) {
  const { rows, keys } = useMemo(
    () =>
      buildSeries({
        comparisonData,
        selectedPathways,
        chartKey: "chartFood1",
      }),
    [comparisonData, selectedPathways],
  );

  // índice por pathway para dashes estables (Feasible lines only)
  const pathwayIndex = useMemo(() => {
    const map = new Map();
    (selectedPathways || []).forEach((p, i) => map.set(p, i));
    return map;
  }, [selectedPathways]);

  // líneas ocultas (por key: métrica fija o "pathway — Feasible kcal")
  const [hiddenLines, setHiddenLines] = useState(() => new Set());

  const toggleLine = (key) => {
    setHiddenLines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const colorForLineKey = (k) => {
    if (isFixedKey(k)) return METRIC_COLOR[k] || "#9999CC";
    // Feasible: use pathway color
    const pathway = pathwayFromKey(k);
    return pathway && getColorByName ? getColorByName(pathway) : "#97E6BA";
  };

  const dashForLineKey = (k) => {
    if (isFixedKey(k)) return "5 5"; // fixed lines dashed
    const pathway = pathwayFromKey(k);
    const idx = pathwayIndex.get(pathway) ?? 0;
    return DASH_POOL[idx % DASH_POOL.length];
  };

  const isLineHidden = (k) => hiddenLines.has(k);

  // nombre bonito en tooltip/legend
  const formatLineName = (k) => {
    if (isFixedKey(k)) return k;
    const pathway = pathwayFromKey(k);
    return pathway ? `${pathway}` : k;
  };

  const [analysis, setAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    if (!rows?.length) return;

    if (!selectedPathways || selectedPathways.length < 2) {
      setAnalysis("Please select at least two pathways to compare analysis.");
      setLoadingAnalysis(false);
      return;
    }

    let isMounted = true;
    const fetchAnalysis = async () => {
      setLoadingAnalysis(true);
      try {
        const payload = {
          context: "food_intake",
          dataset: rows,
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL || ""}/api/chat/fablegpt-comparison`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        if (!response.ok) throw new Error("Analysis API error");
        const resData = await response.json();
        if (isMounted) {
          setAnalysis(
            resData.analysis?.replace(/_/g, " ") || "No analysis available.",
          );
        }
      } catch (error) {
        console.error("Error fetching comparison analysis:", error);
        if (isMounted) setAnalysis("Cannot load analysis at this time.");
      } finally {
        if (isMounted) setLoadingAnalysis(false);
      }
    };

    fetchAnalysis();
    return () => {
      isMounted = false;
    };
  }, [rows, selectedPathways]);

  if (!rows?.length || !keys?.length) {
    return (
      <div className="rounded-xl bg-slate-50 p-6 ring-1 ring-slate-200 text-sm text-slate-700">
        No data for Food intake chart.
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white px-6 py-5 ring-1 ring-[#E3E5E6] shadow-sm">
      <div className="text-[14px] font-semibold text-[#101010] mb-3">
        Daily per capita food intake
      </div>

      <div style={{ width: "100%", height: 440 }}>
        <ResponsiveContainer>
          <LineChart
            data={rows}
            margin={{ top: 30, right: 40, left: 10, bottom: 40 }}
          >
            <CartesianGrid
              stroke="#F1F3F4"
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              dataKey="year"
              tick={{ fill: "#7E8081", fontSize: 12 }}
              tickMargin={12}
              axisLine={{ stroke: "#E3E5E6" }}
              tickLine={false}
            >
              <Label
                value="Years"
                position="insideBottom"
                offset={-30}
                style={{
                  fill: "#98999A",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              />
            </XAxis>
            <YAxis
              tick={{ fill: "#7E8081", fontSize: 12 }}
              tickMargin={10}
              axisLine={{ stroke: "#E3E5E6" }}
              tickLine={false}
            >
              <Label
                value="kcal/Person/Day"
                angle={-90}
                position="insideLeft"
                style={{
                  fill: "#98999A",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              />
            </YAxis>
            <Tooltip content={<CustomTooltip pathwayIndex={pathwayIndex} />} />

            {/* ✅ legend arriba + clickable para fijas */}
            <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{ paddingBottom: 8 }}
              content={(props) => (
                <ClickableLegend
                  {...props}
                  hidden={hiddenLines}
                  onToggle={toggleLine}
                  pathwayIndex={pathwayIndex}
                />
              )}
            />

            {keys.map((k) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                name={formatLineName(k)}
                dot={false}
                stroke={colorForLineKey(k)}
                strokeDasharray={dashForLineKey(k)}
                strokeWidth={3}
                activeDot={{ r: 6 }}
                connectNulls
                isAnimationActive={false}
                hide={isLineHidden(k)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="w-full min-h-[100px] mt-4">
        {loadingAnalysis ? (
          <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
            <svg
              className="animate-spin h-5 w-5 text-[#59AD31]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              ></path>
            </svg>
            <span className="font-bold">Generating analysis...</span>
          </div>
        ) : (
          <div className="group relative">
            <NativeAnalysis content={analysis} />
          </div>
        )}
      </div>
    </div>
  );
}
